<template>
  <div>
    <!-- Book list -->
    <div>
      <vs-table
        id="book-list"
        class="vs-con-loading__container"
        stripe
        :sst="true"
        maxHeight="800px"
        @search="updateSearchQuery"
        @change-page="handleChangePage"
        @sort="handleSort"
        :total="FilteredCount"
        :max-items="length"
        search
        :data="records"
      >
        <template slot="header">
          <div class="mb-2 flex items-center">
            <div class="flex flex-wrap justify-between items-center">
              <div class="mb-4 md:mb-0 mr-4 ag-grid-table-actions-left">
                <vs-dropdown vs-trigger-click class="cursor-pointer filter-font">
                  <div class="p-4 border border-solid d-theme-border-grey-light rounded-lg d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium">
                    <span class="mr-2">
                      {{ page * length - (length - (FilteredCount && 1)) || 0 }}
                      -
                      {{ FilteredCount - page * length > 0 ? page * length : FilteredCount }}
                      of {{ total || 0 }}
                    </span>
                    <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
                  </div>
                  <vs-dropdown-menu>
                    <vs-dropdown-item @click="handleChangeLength(10)">
                      <span>10</span>
                    </vs-dropdown-item>
                    <vs-dropdown-item @click="handleChangeLength(20)">
                      <span>20</span>
                    </vs-dropdown-item>
                    <vs-dropdown-item @click="handleChangeLength(50)">
                      <span>50</span>
                    </vs-dropdown-item>
                    <vs-dropdown-item @click="handleChangeLength(100)">
                      <span>100</span>
                    </vs-dropdown-item>
                  </vs-dropdown-menu>
                </vs-dropdown>
              </div>
            </div>
            <div class="flex flex-wrap items-center">
              <!-- Custom Pagination -->
              <vs-pagination v-if="FilteredCount" v-model="page" :total="totalPages" :max="totalPages / length > 7 ? 7 : 5" class="mt-1"></vs-pagination>
            </div>
          </div>
        </template>

        <template slot="thead">
          <vs-th>Sr#</vs-th>
          <vs-th sort-key="user">User</vs-th>
          <vs-th sort-key="highlight_text">Index</vs-th>
          <vs-th sort-key="content">Original Content</vs-th>
          <vs-th v-if="isPositiveType" sort-key="feedback">User Feedback</vs-th>
          <vs-th sort-key="createdAt">Date</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr :data="tr" :key="i" v-for="(tr, i) in data">
            <vs-td>
              {{ page * length - (length - i - 1) }}
            </vs-td>
            <vs-td class="text-left">
              <span>{{ tr.user.first_name }} {{ tr.user.last_name }}</span>
              <br />
              <small class="text-gray-500">{{ tr.user.email }}</small>
            </vs-td>
            <vs-td class="text-left">
              {{ tr.highlight_text || '-' }}
            </vs-td>
            <vs-td class="text-left">
              {{ tr.content || '-' }}
            </vs-td>
            <vs-td v-if="isPositiveType" class="text-left cursor-pointer">
              <span class="cursor-pointer" @click="openDetailModal(tr)" v-tooltip="{ content: `View Feedback` }">
                {{ tr.feedback || '-' }}
              </span>
            </vs-td>
            <vs-td class="text-left">
              {{ formatDateTime(tr.createdAt) }}
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </div>

    <!-- Detail Modal -->
    <vs-popup :active.sync="isDetailModalActive" title="Feedback Details" button-accept="Close" @accept="isDetailModalActive = false" width="500px">
      <div v-if="selectedRecord" class="space-y-3">
        <!-- User Info -->
        <div class="flex gap-1 items-center border-b border-gray-200 pb-1">
          <span class="text-sm text-gray-500 font-medium">User:</span>
          <span class="text-lg font-semibold text-gray-800"> {{ selectedRecord.user.first_name }} {{ selectedRecord.user.last_name }} </span>
        </div>

        <!-- Email -->
        <div class="flex gap-1 items-center border-b border-gray-200 pb-1">
          <span class="text-sm text-gray-500 font-medium">Email:</span>
          <span class="text-lg font-semibold text-gray-800">{{ selectedRecord.user.email || '-' }}</span>
        </div>

        <!-- Index -->
        <div class="flex gap-1 items-start border-b border-gray-200 pb-1">
          <span class="text-sm text-gray-500 font-medium">Index:</span>
          <span class="text-lg font-semibold text-gray-800">{{ selectedRecord.highlight_text || '-' }}</span>
        </div>

        <!-- Original Content -->
        <div class="flex gap-1 items-start border-b border-gray-200 pb-1">
          <span class="text-sm text-gray-500 font-medium">Original Content:</span>
          <span class="text-lg font-semibold text-gray-800">{{ selectedRecord.content || '-' }}</span>
        </div>

        <!-- Feedback (only shown for positive type) -->
        <div v-if="isPositiveType" class="flex gap-1 items-start border-b border-gray-200 pb-1">
          <span class="text-sm text-gray-500 font-medium">User Feedback :</span>
          <span class="text-lg font-semibold text-gray-800">{{ selectedRecord.feedback || '-' }}</span>
        </div>

        <!-- Date -->
        <div class="flex gap-1 items-center border-b border-gray-200 pb-1">
          <span class="text-sm text-gray-500 font-medium">Date:</span>
          <span class="text-lg font-semibold text-gray-800">{{ formatDateTime(selectedRecord.createdAt) }}</span>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { dateMixins } from '@/mixins/dateMixins.js'

export default {
  name: 'highlightFeedbackList',
  mixins: [dateMixins],

  /** Data */
  data: () => ({
    order: [],
    length: 100,
    page: 1,
    search: '',
    module_name: 'Highlight Feedback',
    active: false,
    active2: true,
    active3: false,
    selected: [],
    users: [],
    breadcrumb: [],
    selectedRecord: null,
    isDetailModalActive: false
  }),

  /** computed */
  computed: {
    isPositiveType() {
      return this.$route.params.type !== 'positive'
    },
    ...mapState('book', ['records', 'total', 'FilteredCount', 'listLoading']),
    totalPages() {
      return Math.ceil(this.FilteredCount / this.length)
    }
  },

  /** Functions */
  methods: {
    ...mapActions('book', {
      get: 'highlightFeedbackList',
      delete: 'delete'
    }),

    /** Per Page Limit Change */
    handleChangeLength(length) {
      this.page = 1
      this.length = length
      this.getData()
    },

    /** Page Change */
    handleChangePage(page) {
      this.page = page
      this.getData()
    },

    /** Sorting Table Data */
    handleSort(key, active) {
      if (!key) return
      this.page = 1
      this.order = [key, active === 'desc' ? 'DESC' : 'ASC']
      this.getData()
    },

    /** Search Query */
    updateSearchQuery(val) {
      this.page = 1
      this.search = val
      this.getData()
    },

    openDetailModal(record) {
      this.selectedRecord = record
      this.isDetailModalActive = true
    },

    /** Book List API */
    async getData() {
      await this.get({
        params: {
          limit: this.length,
          page: this.page,
          type: this.$route.params.type == 'positive' ? 'negative' : 'positive',
          search: this.search
        },
        book: this.$route.params.id
      })
    }
  },

  /** Watchers */
  watch: {
    breadcrumb(newvalue) {
      this.$emit('update-breadcrumb', newvalue)
    },
    listLoading() {
      if (this.listLoading) {
        this.$vs.loading({
          container: '#book-list',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#book-list > .con-vs-loading')
      }
    },
    page() {
      this.getData()
    }
  },

  /** On Rendering */
  async mounted() {
    await this.getData()
    const bookId = this.$route.params.id
    if (bookId) {
      await this.$store.dispatch('book/show', bookId).then(async (res) => {
        const data = res.data.data
        this.breadcrumb = [
          { title: 'Dashboard', url: '/dashboard' },
          { title: 'Book', url: '/book/list' },
          { title: data.title, active: true },
          { title: 'Highlight Feedback', active: true }
        ]
      })
    }
  }
}
</script>
